"use client";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { message } from "antd";
import {
	Search,
	Badge,
	CheckCircle2,
	ChevronLeft,
	ChevronRight,
} from "lucide-react";
import { Trait, DigitalAsset } from "@/app/types";
import debounce from "lodash/debounce";
import LazyNFTMedia from "../UI/lazyLoadImage";
import Link from "next/link";
import { createUmiInstance } from "@/lib/umi";
import {
	LARGE_COLLECTION_THRESHOLD,
	CHUNK_SIZES,
	PROCESSING_DELAYS,
	API_LIMITS,
} from "@/constants/cnft.constants";
import { useOptimizedCNFTFetch } from "@/hooks/useOptimizedCNFTFetch";
import { usePrivyWallet } from "@/hooks/usePrivyWallet";

interface CompressedNFTProps {
	selectedNFTs: DigitalAsset[];
	setSelectedNFTs: (nfts: DigitalAsset[]) => void;
}

const CompressedNFT = ({
	selectedNFTs,
	setSelectedNFTs,
}: CompressedNFTProps) => {
	const { signer, publicKey, user } = usePrivyWallet();
	// State
	const [compressedNFTs, setCompressedNFTs] = useState<DigitalAsset[]>([]);
	const [selectedTraits, setSelectedTraits] = useState<Trait[]>([]);
	const [traits, setTraits] = useState<Trait[]>([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [errorCount, setErrorCount] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [loadingProgress, setLoadingProgress] = useState(0);
	const [totalCount, setTotalCount] = useState(0);
	const [fetchStrategy, setFetchStrategy] = useState<
		"count" | "fetch" | "complete"
	>("count");
	const [isHydrated, setIsHydrated] = useState(false);
	const itemsPerPage = 20;

	// Prevent hydration mismatch
	useEffect(() => {
		setIsHydrated(true);
	}, []);

	const rpcEndpoint =
		process.env.NEXT_PUBLIC_ENV === "local"
			? process.env.NEXT_PUBLIC_DEVELOPMENT_RPC
			: process.env.NEXT_PUBLIC_PRODUCTION_RPC;

	const ESCROW_WALLET = process.env.NEXT_PUBLIC_ESCROW_ACCOUNT;

	const isValidNFTMetadata = (item: any): boolean => {
		try {
			if (!item || !item.id || !item.content) {
				return false;
			}

			if (!item.compression || !item.compression.compressed) {
				return false;
			}

			const currentWallet = publicKey?.toString();
			if (currentWallet === ESCROW_WALLET) {
				return true;
			}

			if (
				ESCROW_WALLET &&
				item.ownership &&
				!(
					item.ownership.owner ===
						"E6oewvmmRMy3vB7FyvPLFz7GPZfCYmYiS8k4LGJFFWf4" ||
					item.ownership.delegate === ESCROW_WALLET
				)
			) {
				return false;
			}
			return true;
		} catch (error) {
			return false;
		}
	};

	const safelyExtractMetadata = useCallback(
		async (item: any): Promise<DigitalAsset | null> => {
			try {
				if (!isValidNFTMetadata(item)) {
					return null;
				}

				const metadataFromResponse = item.content?.metadata || {};

				// Get image URI from the response
				const imageUri =
					item.content?.links?.image ||
					item.content?.files?.[0]?.uri ||
					item.content?.files?.[0]?.cdn_uri ||
					"";

				// Get name from the response
				const safeName =
					metadataFromResponse.name || `Compressed NFT #${item.id.slice(0, 8)}`;

				// Get description from the response
				const description = metadataFromResponse.description || "";

				// Get attributes from the response
				const safeAttributes = (() => {
					const attrs = metadataFromResponse.attributes || [];
					return Array.isArray(attrs)
						? attrs.filter(
								(attr: any) =>
									attr &&
									typeof attr === "object" &&
									attr.trait_type &&
									attr.value !== undefined &&
									attr.value !== null
						  )
						: [];
				})();

				return {
					publicKey: item.id,
					mint: item.id,
					isCompressed: Boolean(item.compression?.compressed),
					tokens: [],
					metadata: {
						name: safeName,
						uri: item.content?.json_uri || "",
					},
					metadata_uri: {
						name: safeName,
						image: imageUri,
						description: description,
						attributes: safeAttributes,
						publicKey: item.id,
						owner: item.ownership?.owner || publicKey?.toString() || "",
						mint: item.id,
						updateAuthority: item.authorities?.[0]?.address || "",
					},
				} as unknown as DigitalAsset;
			} catch (error) {
				return null;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[publicKey]
	);

	// Helper function to check if a value should be filtered out
	const shouldFilterOutTrait = useCallback((value: string): boolean => {
		const trimmed = value.trim();

		if (!trimmed || trimmed === "null" || trimmed === "NULL") {
			return true;
		}

		if (
			!isNaN(Number(trimmed)) &&
			!isNaN(parseFloat(trimmed)) &&
			isFinite(Number(trimmed))
		) {
			return true;
		}

		if (/^\d+$/.test(trimmed)) {
			return true;
		}

		if (/^\d+\.\d+$/.test(trimmed)) {
			return true;
		}

		if (trimmed.toLowerCase() === "true" || trimmed.toLowerCase() === "false") {
			return true;
		}

		if (/^(https?:\/\/|ftp:\/\/|@)/.test(trimmed.toLowerCase())) {
			return true;
		}

		if (
			/^\d+(\.\d+)?\s*(USD[TC]?|EUR|BTC|ETH|SOL|USDT|USDC|\$|€|£|¥)$/i.test(
				trimmed
			)
		) {
			return true;
		}

		if (
			/^\d+\s*(hours?|mins?|minutes?|secs?|seconds?|days?|hrs?|h|m|s)(\s+\d+\s*(hours?|mins?|minutes?|secs?|seconds?|days?|hrs?|h|m|s))*[!\.]?$/i.test(
				trimmed
			)
		) {
			return true;
		}

		if (/^\d{1,3}(,\d{3})*(\.\d+)?$/.test(trimmed)) {
			return true;
		}

		if (/^\d+(\.\d+)?[KMBTkmbt]$/i.test(trimmed)) {
			return true;
		}

		if (
			/^[a-zA-Z0-9-]+\.(com|org|net|io|xyz|app|co|me|tv|gg|ly|dev|ai|tech)$/i.test(
				trimmed
			)
		) {
			return true;
		}

		return false;
	}, []);

	const addUniqueTraits = useCallback(
		(newTraits: Trait[]) => {
			if (!Array.isArray(newTraits) || newTraits.length === 0) return;

			setTraits((prevTraits) => {
				const seen = new Set(
					prevTraits.map((trait) => `${trait.trait_type}_${trait.value}`)
				);
				const updatedTraits = [...prevTraits];

				newTraits.forEach((trait) => {
					if (
						!trait?.trait_type ||
						!trait?.value ||
						typeof trait.trait_type !== "string" ||
						typeof trait.value !== "string" ||
						trait.trait_type.trim() === "" ||
						trait.value.trim() === ""
					) {
						return;
					}

					// Filter out numeric values and nulls
					const trimmedValue = trait.value.trim();
					if (shouldFilterOutTrait(trimmedValue)) {
						return; // Skip this trait
					}

					const traitKey = `${trait.trait_type}_${trimmedValue}`;
					if (!seen.has(traitKey)) {
						seen.add(traitKey);
						updatedTraits.push({
							trait_type: trait.trait_type.trim(),
							value: trimmedValue,
						});
					}
				});

				return updatedTraits;
			});
		},
		[shouldFilterOutTrait]
	);

	// Get count of compressed NFTs for strategy decision
	const getCompressedNFTCount = useCallback(async (): Promise<number> => {
		if (!publicKey || !rpcEndpoint || !signer) return 0;

		try {
			// Quick count check using UMI first
			if (!signer?.publicKey) {
				throw new Error("Wallet signer not available");
			}

			// Create UMI-compatible signer with signAllTransactions
			const umiSigner = {
				...signer,
				publicKey: signer.publicKey as any, // Type assertion for UMI compatibility
				signAllTransactions: async (transactions: any[]) => {
					const signedTransactions = [];
					for (const transaction of transactions) {
						const signed = await signer.signTransaction(transaction);
						signedTransactions.push(signed);
					}
					return signedTransactions;
				},
			};
			const umi = createUmiInstance(umiSigner);
			// @ts-ignore - UMI types might not be fully updated
			const rpcAssetList = await umi.rpc.getAssetsByOwner({
				owner: publicKey.toString(),
				limit: 1, // Only get count, not actual data
			});

			const totalCount = rpcAssetList.total || 0;
			return totalCount;
		} catch (umiError) {
			try {
				// Fallback: Use a lightweight request to check count
				const response = await fetch(
					`${rpcEndpoint}/v0/addresses/${publicKey.toString()}/nfts?compression=true&limit=1`
				);

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				const data = await response.json();
				const count = data.result?.total || data.result?.nfts?.length || 0;
				return count;
			} catch (fallbackError) {
				console.warn("Failed to get count, assuming 0:", fallbackError);
				return 0;
			}
		}
	}, [publicKey, rpcEndpoint, signer]);

	// Process NFTs in chunks to avoid blocking UI
	const processNFTsInChunks = useCallback(
		async (
			nfts: any[],
			chunkSize: number = 50
		): Promise<{
			processedNFTs: DigitalAsset[];
			traits: Trait[];
			errorCount: number;
		}> => {
			const allNFTs: DigitalAsset[] = [];
			const allTraits: Trait[] = [];
			let currentErrorCount = 0;

			for (let i = 0; i < nfts.length; i += chunkSize) {
				const chunk = nfts.slice(i, i + chunkSize);

				// Process chunk
				const chunkPromises = chunk.map(async (item: any) => {
					try {
						const processed = await safelyExtractMetadata(item);
						if (processed) {
							// Extract traits
							const attributes = processed?.metadata_uri?.attributes;
							if (Array.isArray(attributes)) {
								const validTraits = attributes.filter((trait) => {
									if (
										!trait ||
										typeof trait.trait_type !== "string" ||
										typeof trait.value !== "string"
									) {
										return false;
									}

									const trimmedValue = trait.value.trim();
									// Filter out numeric values and nulls
									if (shouldFilterOutTrait(trimmedValue)) {
										return false;
									}

									return true;
								});
								return { processed, traits: validTraits };
							}
							return { processed, traits: [] };
						} else {
							return { processed: null, traits: [] };
						}
					} catch (error) {
						console.warn("Error processing NFT:", error);
						return { processed: null, traits: [] };
					}
				});

				const chunkResults = await Promise.all(chunkPromises);

				// Process results
				chunkResults.forEach((result) => {
					if (result.processed) {
						allNFTs.push(result.processed);
						allTraits.push(...result.traits);
					} else {
						currentErrorCount++;
					}
				});

				// Update progress
				const progress = Math.min(((i + chunkSize) / nfts.length) * 100, 100);
				setLoadingProgress(progress);

				// Small delay to prevent UI blocking
				if (i + chunkSize < nfts.length) {
					await new Promise((resolve) =>
						setTimeout(resolve, PROCESSING_DELAYS.CHUNK_DELAY)
					);
				}
			}

			return {
				processedNFTs: allNFTs,
				traits: allTraits,
				errorCount: currentErrorCount,
			};
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[safelyExtractMetadata]
	);

	// Use the optimized hook
	const optimizedHook = useOptimizedCNFTFetch(
		safelyExtractMetadata,
		addUniqueTraits
	);

	// Simple fetch function that uses the hook
	const fetchCompressedNFTsOptimized = useCallback(async () => {
		if (!publicKey) {
			console.warn("Wallet not connected");
			return;
		}

		const result = await optimizedHook.fetchCompressedNFTs();
		setCompressedNFTs(result);
	}, [publicKey, optimizedHook]);

	// Update local state based on hook progress
	useEffect(() => {
		setIsLoading(optimizedHook.isLoading);
		setLoadingProgress(optimizedHook.progress.progress);
		setTotalCount(optimizedHook.progress.totalCount);
		setFetchStrategy(optimizedHook.progress.strategy);
		setErrorCount(optimizedHook.progress.errorCount);
	}, [optimizedHook]);

	const fetchStandardCollection = useCallback(async () => {
		if (!signer || !publicKey) return;
		const allNFTs: DigitalAsset[] = [];
		const allTraits: Trait[] = [];
		let currentErrorCount = 0;

		try {
			// Try UMI method first with pagination check
			if (!signer?.publicKey) {
				throw new Error("Wallet signer not available");
			}

			// Create UMI-compatible signer with signAllTransactions
			const umiSigner = {
				...signer,
				publicKey: signer.publicKey as any, // Type assertion for UMI compatibility
				signAllTransactions: async (transactions: any[]) => {
					const signedTransactions = [];
					for (const transaction of transactions) {
						const signed = await signer.signTransaction(transaction);
						signedTransactions.push(signed);
					}
					return signedTransactions;
				},
			};
			const umi = createUmiInstance(umiSigner);
			// @ts-ignore - UMI types might not be fully updated
			const rpcAssetList = await umi.rpc.getAssetsByOwner({
				owner: publicKey!.toString(),
				limit: API_LIMITS.FETCH_LIMIT,
			});

			const assets = rpcAssetList.items || [];

			// Check if we need pagination (got exactly the limit)
			if (assets.length === API_LIMITS.FETCH_LIMIT) {
				// This is where the circular dependency was. We will fix this.
				// For now, we remove the call to fetchAllCNFTsWithPagination
				// await fetchAllCNFTsWithPagination();
				return;
			}

			if (assets.length > 0) {
				// Filter compressed assets first
				const compressedAssets = assets.filter(
					(item: any) => item && item.compression && item.compression.compressed
				);

				// Update total count
				setTotalCount(compressedAssets.length);

				// Process in smaller chunks for better responsiveness
				const { processedNFTs, traits, errorCount } = await processNFTsInChunks(
					compressedAssets,
					CHUNK_SIZES.STANDARD // Smaller chunks for better UI responsiveness
				);

				allNFTs.push(...processedNFTs);
				allTraits.push(...traits);
				currentErrorCount += errorCount;
			}
		} catch (umiError) {
			console.warn("UMI method failed, trying fallback:", umiError);

			// Fallback: Use direct RPC call
			try {
				const response = await fetch(
					`${rpcEndpoint}/v0/addresses/${publicKey!.toString()}/nfts?compression=true`
				);

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				const data = await response.json();
				const compressedNFTs = data.result?.nfts || [];

				// Process fallback data in chunks
				const { processedNFTs, traits, errorCount } = await processNFTsInChunks(
					compressedNFTs,
					CHUNK_SIZES.STANDARD
				);

				allNFTs.push(...processedNFTs);
				allTraits.push(...traits);
				currentErrorCount += errorCount;
			} catch (fallbackError) {
				console.error("Fallback method also failed:", fallbackError);
				throw fallbackError;
			}
		}

		message.destroy();
		setCompressedNFTs(allNFTs);
		setErrorCount(currentErrorCount);
		addUniqueTraits(allTraits);

		if (allNFTs.length > 0) {
			message.success(`Loaded ${allNFTs.length} compressed NFTs`);
		} else {
			message.info("No compressed NFTs found in your wallet");
		}

		if (currentErrorCount > 0) {
			console.warn(
				`${currentErrorCount} compressed NFTs had invalid metadata and were filtered out`
			);
		}
	}, [signer, publicKey, rpcEndpoint, processNFTsInChunks, addUniqueTraits]);

	// Fetch all CNFTs with pagination
	const fetchAllCNFTsWithPagination = useCallback(async () => {
		if (!signer) return;
		try {
			if (!signer?.publicKey) {
				throw new Error("Wallet signer not available");
			}

			// Create UMI-compatible signer with signAllTransactions
			const umiSigner = {
				...signer,
				publicKey: signer.publicKey as any, // Type assertion for UMI compatibility
				signAllTransactions: async (transactions: any[]) => {
					const signedTransactions = [];
					for (const transaction of transactions) {
						const signed = await signer.signTransaction(transaction);
						signedTransactions.push(signed);
					}
					return signedTransactions;
				},
			};
			const umi = createUmiInstance(umiSigner);
			let allAssets: any[] = [];
			let page = 1;
			let hasMore = true;
			let totalFetched = 0;

			while (hasMore) {
				try {
					// @ts-ignore - UMI types might not be fully updated
					const rpcAssetList = await umi.rpc.getAssetsByOwner({
						owner: publicKey!.toString(),
						limit: API_LIMITS.FETCH_LIMIT,
						page: page,
					});

					const fetchedAssets = rpcAssetList.items || [];
					const fetchedCount = fetchedAssets.length;

					allAssets.push(...fetchedAssets);
					totalFetched += fetchedCount;

					// Update progress based on fetched count
					const progressPercent = Math.min(
						(totalFetched / Math.max(totalCount, totalFetched)) * 80,
						80
					);
					setLoadingProgress(progressPercent);
					setTotalCount(Math.max(totalCount, totalFetched));

					// If we got less than the limit, we've reached the end
					if (fetchedCount < API_LIMITS.FETCH_LIMIT) {
						hasMore = false;
					} else {
						page++;
					}

					// Add a small delay between pagination requests
					if (hasMore) {
						await new Promise((resolve) => setTimeout(resolve, 100));
					}
				} catch (pageError) {
					console.error(`Error fetching page ${page}:`, pageError);
					hasMore = false;
				}
			}

			// Filter compressed assets first to reduce processing load
			const compressedAssets = allAssets.filter(
				(item: any) => item && item.compression && item.compression.compressed
			);

			// Update total count with actual compressed NFTs count
			setTotalCount(compressedAssets.length);
			setLoadingProgress(80);

			// Process in optimized chunks
			const { processedNFTs, traits, errorCount } = await processNFTsInChunks(
				compressedAssets,
				CHUNK_SIZES.LARGE // Larger chunks for better performance
			);

			message.destroy();
			setCompressedNFTs(processedNFTs);
			setErrorCount(errorCount);
			addUniqueTraits(traits);

			message.success(
				`Loaded ${processedNFTs.length} compressed NFTs (Total fetched: ${totalFetched} assets)`
			);

			if (errorCount > 0) {
				console.warn(
					`${errorCount} compressed NFTs had invalid metadata and were filtered out`
				);
			}
		} catch (error) {
			console.error("Paginated fetch failed:", error);
			// Fallback to standard method
			await fetchStandardCollection();
		}
	}, [
		signer,
		publicKey,
		processNFTsInChunks,
		addUniqueTraits,
		totalCount,
		fetchStandardCollection,
	]);

	// Fetch strategy for large collections (>LARGE_COLLECTION_THRESHOLD NFTs)
	const fetchLargeCollection = useCallback(
		async (estimatedCount: number) => {
			await fetchAllCNFTsWithPagination();
		},
		[fetchAllCNFTsWithPagination]
	);

	// Fetch strategy for standard collections (≤LARGE_COLLECTION_THRESHOLD NFTs)
	// This is now defined above fetchAllCNFTsWithPagination

	// Initial fetch when wallet is connected - using ref to prevent infinite loops
	const hasInitialFetch = useRef(false);

	useEffect(() => {
		if (publicKey && !hasInitialFetch.current) {
			hasInitialFetch.current = true;
			fetchCompressedNFTsOptimized();
		}

		// Reset the ref when wallet disconnects
		if (!publicKey) {
			hasInitialFetch.current = false;
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [publicKey]);

	// Reset state when wallet disconnects
	useEffect(() => {
		if (!publicKey) {
			setCompressedNFTs([]);
			setSelectedTraits([]);
			setTraits([]);
			setSearchTerm("");
			setErrorCount(0);
			setCurrentPage(1);
		}
	}, [publicKey]);

	// Filter NFTs based on search and traits
	const filteredNFTs = useMemo(() => {
		let filtered = [...compressedNFTs];

		// Remove NFT name search filtering - search now only applies to traits
		// if (searchTerm.trim()) {
		// 	filtered = filtered.filter((nft) =>
		// 		nft?.metadata?.name
		// 			?.toLowerCase()
		// 			.includes(searchTerm.toLowerCase().trim())
		// 	);
		// }

		if (selectedTraits.length > 0) {
			filtered = filtered.filter((nft) => {
				const nftAttributes = nft?.metadata_uri?.attributes;
				if (!Array.isArray(nftAttributes)) return false;

				return selectedTraits.every((selectedTrait) =>
					nftAttributes.some(
						(nftTrait) =>
							nftTrait?.trait_type?.toLowerCase() ===
								selectedTrait.trait_type?.toLowerCase() &&
							nftTrait?.value?.toLowerCase() ===
								selectedTrait.value?.toLowerCase()
					)
				);
			});
		}

		return filtered;
	}, [compressedNFTs, selectedTraits]);

	// Filter traits based on search term
	const filteredTraits = useMemo(() => {
		if (!searchTerm.trim()) {
			return traits;
		}

		return traits.filter(
			(trait) =>
				trait.value.toLowerCase().includes(searchTerm.toLowerCase().trim()) ||
				trait.trait_type.toLowerCase().includes(searchTerm.toLowerCase().trim())
		);
	}, [traits, searchTerm]);

	// Paginated NFTs
	const paginatedNFTs = useMemo(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		return filteredNFTs.slice(startIndex, startIndex + itemsPerPage);
	}, [filteredNFTs, currentPage, itemsPerPage]);

	const totalPages = Math.ceil(filteredNFTs.length / itemsPerPage);

	// Handle page change
	const handlePageChange = (newPage: number) => {
		if (newPage >= 1 && newPage <= totalPages) {
			setCurrentPage(newPage);
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
	};

	// Reset page when filters change
	useEffect(() => {
		setCurrentPage(1);
	}, [selectedTraits, filteredNFTs.length]);

	// Handle trait selection
	const handleTraitSelection = useCallback((trait: Trait) => {
		if (!trait?.trait_type || !trait?.value) return;

		setSelectedTraits((prev) =>
			prev.some(
				(t) =>
					t?.trait_type?.toLowerCase() === trait.trait_type?.toLowerCase() &&
					t?.value?.toLowerCase() === trait.value?.toLowerCase()
			)
				? prev.filter(
						(t) =>
							!(
								t?.trait_type?.toLowerCase() ===
									trait.trait_type?.toLowerCase() &&
								t?.value?.toLowerCase() === trait.value?.toLowerCase()
							)
				  )
				: [...prev, trait]
		);
	}, []);

	// Check if NFT is selected
	const isNftSelected = useCallback(
		(nft: DigitalAsset) => {
			return selectedNFTs.some(
				(selected) => selected?.publicKey === nft?.publicKey
			);
		},
		[selectedNFTs]
	);

	// Toggle NFT selection
	const toggleNftSelection = useCallback(
		(nft: DigitalAsset) => {
			if (!nft || !nft.publicKey) return;

			if (!nft.isCompressed) {
				message.error(
					"This is a standard NFT. Please use the Standard NFTs tab to select it."
				);
				return;
			}

			const hasStandardNFTs = selectedNFTs.some(
				(selected) => selected && !selected.isCompressed
			);
			if (hasStandardNFTs) {
				message.error(
					"You cannot mix standard and compressed NFTs. Please clear your selection first."
				);
				return;
			}

			setSelectedNFTs(
				isNftSelected(nft)
					? selectedNFTs.filter(
							(selected) => selected?.publicKey !== nft.publicKey
					  )
					: [...selectedNFTs, nft]
			);
		},
		[selectedNFTs, setSelectedNFTs, isNftSelected]
	);

	// Select all filtered NFTs
	const selectAllFilteredNFTs = useCallback(() => {
		const compressedFilteredNFTs = filteredNFTs.filter(
			(nft) => nft && nft.publicKey && nft.isCompressed
		);

		const hasStandardNFTs = selectedNFTs.some(
			(selected) => selected && !selected.isCompressed
		);
		if (hasStandardNFTs) {
			message.error(
				"You cannot mix standard and compressed NFTs. Please clear your selection first."
			);
			return;
		}

		const allFilteredSelected = compressedFilteredNFTs.every((nft) =>
			isNftSelected(nft)
		);

		if (allFilteredSelected) {
			setSelectedNFTs(
				selectedNFTs.filter(
					(nft) =>
						!compressedFilteredNFTs.some(
							(filtered) => filtered.publicKey === nft?.publicKey
						)
				)
			);
		} else {
			const newSelection = new Set([...selectedNFTs]);
			compressedFilteredNFTs.forEach((nft) => newSelection.add(nft));
			setSelectedNFTs(Array.from(newSelection));
		}
	}, [filteredNFTs, selectedNFTs, setSelectedNFTs, isNftSelected]);

	// Debounced search
	const debouncedSetSearchTerm = useMemo(
		() => debounce((term: string) => setSearchTerm(term), 300),
		[]
	);

	useEffect(() => {
		return () => {
			debouncedSetSearchTerm.cancel();
		};
	}, [debouncedSetSearchTerm]);

	// Counts
	const compressedNFTCount = compressedNFTs.length;
	const selectedCompressedCount = selectedNFTs.filter(
		(nft) => nft && nft.isCompressed
	).length;

	// Show loading state during hydration
	if (!isHydrated) {
		return (
			<div className="text-center py-12">
				<div className="animate-spin size-8 border-4 border-mgt-green border-t-transparent rounded-full mx-auto mb-4" />
				<p className="text-gray-400">Loading...</p>
			</div>
		);
	}

	return (
		<div>
			{/* Discord Support Notice */}
			<div className="bg-blue-500 bg-opacity-10 border border-blue-500 rounded-xl p-4 mb-6">
				<div className="flex items-start gap-3">
					<div className="text-blue-400 mt-0.5">
						<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
							<path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0189 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z" />
						</svg>
					</div>
					<div className="flex-1">
						<h3 className="text-blue-400 font-medium text-sm mb-1">
							Need help with compressed NFTs?
						</h3>
						<p className="text-blue-300 text-sm">
							For support with cNFT drops, please submit a ticket in our Discord
							community at{" "}
							<a
								href="https://discord.gg/UpxTdUVkKy"
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-400 hover:text-blue-300 underline"
							>
								discord.gg/UpxTdUVkKy
							</a>
						</p>
					</div>
				</div>
			</div>

			<div className="mb-8">
				<div className="flex items-center justify-between mb-6">
					<h2 className="text-xl font-bold text-white">
						Compressed NFTs{" "}
						<span className="text-mgt-green">
							({compressedNFTCount}
							{totalCount > 0 &&
								totalCount !== compressedNFTCount &&
								` of ${totalCount}`}
							)
						</span>
						{errorCount > 0 && (
							<span className="text-yellow-500 text-xs ml-2">
								({errorCount} Does not meet MGT Criteria -{" "}
								<Link href="/terms">T&Cs</Link>)
							</span>
						)}
						{totalCount > LARGE_COLLECTION_THRESHOLD &&
							fetchStrategy === "complete" && (
								<span className="text-green-400 text-xs ml-2">
									(Large collection optimized)
								</span>
							)}
					</h2>
					<span className="text-white text-sm">
						{selectedCompressedCount} selected
					</span>
				</div>

				{/* Search and filters */}
				<div className="flex flex-col gap-4 sm:flex-row sm:items-center">
					<div className="relative flex-1">
						<input
							type="text"
							placeholder="Search traits..."
							onChange={(e) => debouncedSetSearchTerm(e.target?.value || "")}
							className="w-full bg-[#1A1A1A] text-white px-4 py-3 pl-12 rounded-xl border-none focus:ring-2 focus:ring-mgt-green outline-none"
							aria-label="Search traits"
						/>
						<Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 size-5" />
					</div>
				</div>

				<div className="flex flex-col gap-4 sm:flex-row sm:items-center my-2">
					<div className="flex-1">
						<div className="flex flex-wrap gap-2">
							{filteredTraits.map((trait, index) => (
								<button
									key={`${trait.trait_type}-${trait.value}-${index}`}
									onClick={() => handleTraitSelection(trait)}
									className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                    ${
											selectedTraits.some(
												(t) =>
													t?.trait_type === trait?.trait_type &&
													t?.value === trait?.value
											)
												? "bg-mgt-green text-black"
												: "bg-[#1A1A1A] text-white hover:bg-[#2A2A2A]"
										}`}
								>
									{trait?.value || "Unknown"}
								</button>
							))}
						</div>
					</div>
				</div>
			</div>

			{filteredNFTs.length > 0 && (
				<div className="bg-[#1A1A1A] rounded-xl p-4 mb-6">
					<div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
						<div className="flex items-center gap-4 w-full sm:w-auto">
							<button
								onClick={selectAllFilteredNFTs}
								className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors 
                  ${
										filteredNFTs.every((nft) => isNftSelected(nft))
											? "bg-[#2A2A2A] text-white hover:bg-[#3A3A3A] border border-gray-600"
											: "bg-mgt-green bg-opacity-10 text-mgt-green hover:bg-opacity-20 border border-mgt-green"
									}`}
							>
								{filteredNFTs.every((nft) => isNftSelected(nft))
									? "Deselect All"
									: "Select All Filtered"}
							</button>
						</div>
					</div>
				</div>
			)}

			{isLoading ? (
				<div className="text-center py-12">
					<div className="animate-spin size-8 border-4 border-mgt-green border-t-transparent rounded-full mx-auto mb-4" />
					<p className="text-gray-400">
						{fetchStrategy === "count" && "Checking NFT collection size..."}
						{fetchStrategy === "fetch" &&
							totalCount > LARGE_COLLECTION_THRESHOLD &&
							"Fetching all CNFTs with pagination..."}
						{fetchStrategy === "fetch" &&
							totalCount <= LARGE_COLLECTION_THRESHOLD &&
							totalCount > 0 &&
							"Loading compressed NFTs..."}
						{fetchStrategy === "fetch" &&
							totalCount === 0 &&
							"Loading compressed NFTs..."}
					</p>
					{fetchStrategy === "fetch" && totalCount > 0 && (
						<div className="mt-4 max-w-xs mx-auto">
							<div className="flex justify-between text-sm text-gray-400 mb-2">
								<span>Progress</span>
								<span>{Math.round(loadingProgress)}%</span>
							</div>
							<div className="w-full bg-gray-700 rounded-full h-2">
								<div
									className="bg-mgt-green h-2 rounded-full transition-all duration-300 ease-out"
									style={{ width: `${loadingProgress}%` }}
								/>
							</div>
							{totalCount > LARGE_COLLECTION_THRESHOLD && (
								<p className="text-xs text-gray-500 mt-2">
									Fetching {totalCount} CNFTs...
								</p>
							)}
						</div>
					)}
				</div>
			) : filteredNFTs.length > 0 ? (
				<>
					<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
						{paginatedNFTs.map((nft, index) => {
							if (!nft || !nft.publicKey) return null;

							return (
								<div
									key={nft.publicKey}
									onClick={() => toggleNftSelection(nft)}
									className={`bg-[#1A1A1A] rounded-xl overflow-hidden cursor-pointer transition-transform hover:scale-[1.02] relative
                  ${
										isNftSelected(nft)
											? "ring-2 ring-mgt-green"
											: "hover:ring-1 hover:ring-gray-500"
									}`}
									role="button"
									tabIndex={0}
									aria-pressed={isNftSelected(nft)}
									aria-label={`Select ${nft?.metadata?.name || "NFT"}`}
								>
									<div className="aspect-square relative">
										<LazyNFTMedia
											src={nft?.metadata_uri?.image || ""}
											alt={nft?.metadata_uri?.name || "Compressed NFT"}
											isSelected={isNftSelected(nft)}
											isCompressed={true}
											width={300}
											height={300}
											quality={85}
											loading={index < 8 ? "eager" : "lazy"}
											className="w-full h-full"
										/>
										{isNftSelected(nft) && (
											<div className="absolute top-2 right-2">
												<CheckCircle2 className="size-6 text-mgt-green" />
											</div>
										)}
									</div>
									<div className="p-3">
										<h3 className="text-sm font-medium text-white truncate">
											{nft?.metadata_uri?.name ||
												`NFT #${nft.publicKey.slice(0, 8)}`}
										</h3>
									</div>
								</div>
							);
						})}
					</div>

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="flex justify-center items-center gap-4 mt-8 mb-8">
							<button
								onClick={() => handlePageChange(currentPage - 1)}
								disabled={currentPage === 1}
								className="p-2 rounded-lg bg-[#1A1A1A] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2A2A2A] transition-colors"
								aria-label="Previous page"
							>
								<ChevronLeft className="size-5" />
							</button>

							<span className="text-white">
								Page {currentPage} of {totalPages}
							</span>

							<button
								onClick={() => handlePageChange(currentPage + 1)}
								disabled={currentPage === totalPages}
								className="p-2 rounded-lg bg-[#1A1A1A] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2A2A2A] transition-colors"
								aria-label="Next page"
							>
								<ChevronRight className="size-5" />
							</button>
						</div>
					)}
				</>
			) : (
				<div className="text-center py-12 bg-[#1A1A1A] rounded-xl p-10">
					<Badge className="size-12 text-gray-500 mx-auto mb-4" />
					<h3 className="text-xl font-semibold text-white mb-2">
						No Compressed NFTs Found
					</h3>
					<p className="text-gray-400">
						{selectedTraits.length > 0
							? "Try adjusting your trait filters"
							: "No compressed NFTs found in your wallet"}
					</p>
					{errorCount > 0 && (
						<p className="text-yellow-500 text-sm mt-2">
							{errorCount} NFTs were filtered out due to invalid metadata
						</p>
					)}
					{publicKey && (
						<button
							className="my-5 bg-mgt-green rounded-xl py-3 px-2 text-black hover:bg-opacity-90 transition-opacity disabled:opacity-50 w-full"
							onClick={fetchCompressedNFTsOptimized}
						>
							Refresh NFTs
						</button>
					)}
				</div>
			)}
		</div>
	);
};

export default CompressedNFT;
